import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { config } from 'react-spring';
import { subDays, subMonths, subYears, format } from 'date-fns';
import Short from 'short-uuid';
import dayjs from 'dayjs';
import { ApolloError } from '@apollo/client';

import { ConfigurationStatusEnum, getCurrentUserQuery } from '/apps/portal/src/generated/portal/graphql';
import { getIssuerConfigurationStatusQuery } from '/apps/portal/src/generated/portal/graphql';

export type QUser = getCurrentUserQuery['currentUser'];

export const downloadFile = (name: string, url: string) => {
  const a = document.createElement('a');
  a.download = name;
  a.href = url;
  a.click();
};

export const parseUrl = (str: string) => `//${str.replace(/https?:\/\//, '')}`;

export const getCookieValue = (name: string) => {
  const result = document.cookie.match(`(^|[^;]+)\\s*${name}\\s*=\\s*([^;]+)`);
  return result ? result.pop() : '';
};

export const validatePartnerId = (user: QUser, partnerId: string | null | undefined) => {
  if (!user) return false;
  if (!partnerId) return false;
  return user.partnerWorkstreams.some((workStream) => workStream.partnerId === partnerId);
};

export const checkDevFlag = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('dev') === 'true';
};

export const hasMagic = () => localStorage.getItem('magic') === 'true';

export const withParams = (path: string) => {
  return `${path}${window.location.search}`;
};

export const hideForInternalUser = (user?: QUser, statusData?: getIssuerConfigurationStatusQuery) => {
  return (
    user?.userType === 'Disney' &&
    (statusData?.issuerConfigurationStatus?.status === ConfigurationStatusEnum.Draft ||
      statusData?.issuerConfigurationStatus === null)
  );
};

type cleanInputConfig = {
  removeEmptyArray?: boolean;
};
export const cleanInput = <T extends Record<string, any>>(obj: T, config: cleanInputConfig = {}): T => {
  const { removeEmptyArray } = config;
  const cleanObj: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === 'string') {
      cleanObj[key] = DOMPurify.sanitize(value.trim());
    } else if (Array.isArray(value)) {
      if (removeEmptyArray && value.length === 0) return;

      cleanObj[key] = value.map((item: any) => {
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          return cleanInput(item, config);
        }
        if (typeof item === 'string') {
          return DOMPurify.sanitize(item.trim());
        }
        return item;
      });
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      cleanObj[key] = cleanInput(value, config);
    } else {
      cleanObj[key] = value;
    }
  });

  return cleanObj as T;
};

export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html);
};

export const stringIsJSON = (str: string) => {
  try {
    const type = Object.prototype.toString.call(JSON.parse(str));
    return type === '[object Object]';
  } catch (err) {
    return false;
  }
};

export const renderCodeBlock = (str: string) => {
  return `<pre class="rich-text-code-block"><code>${str}</code></pre>`;
};
export const getHTMLFromRichText = (str: string) => {
  if (stringIsJSON(str)) {
    const contentState = convertFromRaw(JSON.parse(str));
    return stateToHTML(contentState, {
      blockRenderers: {
        'code-block': (block) => renderCodeBlock(block.getText()),
      },
      inlineStyles: {
        CODE: {
          element: 'code',
          attributes: { class: 'rich-text-inline-code' },
        },
      },
    });
    // return stateToHTML(contentState);
  }

  return `<p>${str}</p>`;
};

export const getDownloadFileName = (uri: string) => uri.substring(uri.lastIndexOf('/') + 1);

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const sendMultipleFiles = (
  s3UploadUrl: string,
  file: File,
  handleProgress: (p: number) => void,
  controllerRef: React.RefObject<AbortController>,
) => {
  return axios.put(s3UploadUrl, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progress) => {
      const { loaded, total = 1 } = progress;
      handleProgress(Math.round((loaded * 100) / total));
    },
    signal: controllerRef.current?.signal,
  });
};

export function arraymove(arr: any[], fromIndex: number, toIndex: number) {
  const element = arr[fromIndex];
  const newArray = [...arr];
  newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, element);
  return newArray;
}

export function clamp(number: number, lower: number, upper: number) {
  if (number < lower) {
    return lower;
  } else if (number > upper) {
    return upper;
  }
  return number;
}

export const listDragAndDropAnimator =
  (order: number[], size: number, active = false, originalIndex = 0, curIndex = 0, y = 0) =>
  (index: number) =>
    active && index === originalIndex
      ? {
          y: curIndex * size + y,
          scale: 1.1,
          zIndex: 1,
          shadow: 15,
          immediate: (key: string) => key === 'zIndex',
          config: (key: string) => (key === 'y' ? config.stiff : config.default),
        }
      : {
          y: order.indexOf(index) * size,
          scale: 1,
          zIndex: 0,
          shadow: 1,
          immediate: false,
        };

export const timestampFormatter = (timestamp: number) => {
  return format(timestamp, 'MMM d, yyyy hh:mm a');
};

/**
 * @deprecated
 * @param color
 * @returns
 */
export const getSubtleColor = (color: string): string =>
  `var(--subtle-background-color, linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), ${color})`;

export const containsHTML = (str: string) => /<\/?[a-z][\s\S]*>/i.test(str);

// Adds a space before uppercase letters in a string. Used to format enum vales.
//  example: "InProgress" will return "In Progress"
export const addSpaceBeforeUppercase = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
};

/**
 * Adds spaces before all capital letters in a camelCase string.
 *
 * @param str - The camelCase string to format
 * @returns The formatted string with spaces before capital letters
 *
 * @example
 * addSpacesToCamelCase('camelCaseString') // returns 'camel Case String'
 * addSpacesToCamelCase('firstName') // returns 'first Name'
 * addSpacesToCamelCase('XMLHttpRequest') // returns 'X M L Http Request'
 */
export const addSpacesToCamelCase = (str: string): string => {
  return str.replace(/([A-Z])/g, ' $1').trim();
};

// Generate an array of unique values
export const uniqueArray = <T>(src: T[]): T[] => Array.from(new Set(src));

export const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const decodeBase64UrlSafe = (str: string): string => str.replaceAll('-', '+').replaceAll('_', '/');

export const logError = (error: unknown) => process.env.NODE_ENV !== 'test' && console.error(error);

export const sortAZ = (a: string, b: string) => (a.toLowerCase() < b.toLowerCase() ? -1 : 1);

/**
 * Returns true when the search term is empty or absent, or when `value` contains the term (case-insensitive).
 */
export const matchSearch = (value: string, term: string | null | undefined): boolean => {
  if (term === undefined || term === '' || term === null) return true;
  return value.toLowerCase().includes(term.toLowerCase().trim());
};

export const deepCompare = (obj1: any, obj2: any) => {
  if (obj1 === obj2) return true; // Same reference or both are null

  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false; // If one of them is not an object or is null, they are different
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false; // Different number of keys

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
      return false; // Recursive check for values and missing keys
    }
  }

  return true;
};

/**
 * Finds the closest ancestor element (including the element itself)
 * matching a specific HTML tag name.
 *
 * @template K - A valid HTML tag name (e.g., 'div', 'li', 'p').
 * @param {HTMLElement} element - The starting element from which to search upwards.
 * @param {K} tag - The HTML tag name to search for.
 * @returns {HTMLElementTagNameMap[K] | null} The closest ancestor element matching the tag,
 * strongly typed according to the tag name provided,
 * or null if no matching ancestor is found.
 */
export const getClosestElement = <K extends keyof HTMLElementTagNameMap>(
  element: HTMLElement,
  tag: K,
): HTMLElementTagNameMap[K] | null => {
  const closestMatch = element.closest(tag);

  return closestMatch as HTMLElementTagNameMap[K] | null;
};

/**
 * Returns a new array containing all elements from `array1` that are not present in `array2`.
 * The order of elements from `array1` is preserved.
 *
 * @param array1 The array from which to subtract elements.
 * @param array2 The array containing elements to be removed from `array1`.
 * @returns A new array with the subtracted elements.
 */
export const subtractArrays = <T>(array1: T[], array2: T[]): T[] => {
  const set2 = new Set(array2);

  return [...array1.filter((item) => !set2.has(item))];
};

/**
 * Compares two arrays of strings to determine if they contain the same elements,
 * regardless of order. Takes duplicates into account, so ['a', 'b', 'a'] is not
 * equal to ['a', 'b'].
 *
 * Assumes arrays are small, so sorting is fast and readable.
 *
 * @param a - First array of strings
 * @param b - Second array of strings
 * @returns true if arrays contain the same elements in any order; false otherwise
 */

export const arraysEqualIgnoreOrder = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.every((val, i) => val === sortedB[i]);
};

//  An array of keys to keep in the search parameters.
export const persistedSearchParams = ['dev'];

/**
 * Filters search parameters to only include an accepted list of keys.
 *
 * @param searchParams - The URLSearchParams object or string to filter.
 * @returns A string representation of the filtered search parameters.
 */
export const filterSearchParams = (searchParams: string | URLSearchParams): string => {
  const params = typeof searchParams === 'string' ? new URLSearchParams(searchParams) : searchParams;
  const filteredParams = new URLSearchParams();

  persistedSearchParams.forEach((param) => {
    if (params.has(param)) {
      filteredParams.set(param, params.get(param) as string);
    }
  });

  return filteredParams.toString().length > 0 ? `?${filteredParams.toString()}` : '';
};

/**
 * Remove mark HTML tags contained in a string
 *
 * @param str The string to validate
 * @returns String version without "mark" HTML tags
 */
export const removeHtmlMark = (str: string): string => str.replace(/<[^>]*>/g, '').trim();

/*
  Functions to translate between UUIDs and short IDs using the short-uuid library.
*/

export const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const translateToShortId = (id: string) => {
  const translator = Short();

  return isUUID(id) ? translator.fromUUID(id) : id;
};

export const translateToUUID = (id: string) => {
  const translator = Short(Short.constants.flickrBase58);

  return translator.validate(id) ? translator.toUUID(id) : id;
};

export const isDefined = <T>(value: T | null | undefined): value is T => {
  return value != null;
};

/**
 * Sorts an array of objects by a specified property key.
 *
 * @template T The type of the objects in the array.
 * @param {T[]} items The array of objects to sort.
 * @param {keyof T} key The key of the property to sort by.
 * @param {'asc' | 'desc'} [order='asc'] The sort order. Defaults to 'asc'.
 * @returns {T[]} A new array containing the sorted objects.
 */
export const sortByProperty = <T>(items: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] => {
  // Create a shallow copy to avoid mutating the original array
  const sortedItems = [...items];
  const modifier = order === 'asc' ? 1 : -1;

  sortedItems.sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    // This generic comparison works for numbers, strings, and dates
    if (valueA < valueB) {
      return -1 * modifier;
    }
    if (valueA > valueB) {
      return 1 * modifier;
    }
    return 0;
  });

  return sortedItems;
};

// DateRangeOption is a union type representing predefined date range options
// that can be used to filter or calculate dates relative to the current date.
// These options include:
// - 'Today': Represents the current day.
// - 'Last 7 days': Represents the last 7 days from today.
// - 'Last 15 days': Represents the last 15 days from today.
// - 'Last 30 days': Represents the last 30 days from today.
// - 'Last 3 months': Represents the last 3 months from today.
// - 'Last year': Represents the last year from today.

export type DateRangeOption = 'Today' | 'Last 7 days' | 'Last 15 days' | 'Last 30 days' | 'Last 3 months' | 'Last year';

/**
 * Converts a given DateRangeOption into a formatted date string in UTC.
 *
 * @param range - The date range option to calculate the date for.
 * @returns A number representing the calculated date in epoch format.
 *
 * Example:
 * - 'Today' returns the current date and time in UTC.
 * - 'Last 7 days' returns the date 7 days prior to today in epoch.
 *
 * @throws An error if the provided range is invalid.
 */

export const getDateFromFilterValue = (range: DateRangeOption): number | undefined => {
  const now = new Date();

  switch (range) {
    case 'Today':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    case 'Last 7 days':
      return subDays(now, 7).getTime();
    case 'Last 15 days':
      return subDays(now, 15).getTime();
    case 'Last 30 days':
      return subDays(now, 30).getTime();
    case 'Last 3 months':
      return subMonths(now, 3).getTime();
    case 'Last year':
      return subYears(now, 1).getTime();
    default:
      return undefined;
  }
};

/**
 * Creates a query string by encoding the entire params object as a Base64 string.
 *
 * @param params - The object to encode. Can be deeply nested.
 * @param key - The query parameter key to use for the encoded data. Defaults to "data".
 * @returns A URL-encoded query string (e.g., "data=eyJuYW1lIjoiSm9obiJ9").
 */
export const createQueryStringBase64 = (params: Record<string, any>, key: string = 'data'): string => {
  try {
    // 1. Convert the JavaScript object into a JSON string.
    const jsonString = JSON.stringify(params);

    // 2. Encode the JSON string into a Base64 string.
    // In browsers, btoa() is used. In Node.js, you'd use Buffer.
    // Let's assume a browser environment for btoa().
    const base64String = btoa(jsonString);

    // 3. URL-encode the key and the Base64 string to ensure they are safe for URLs.
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(base64String);

    return `${encodedKey}=${encodedValue}`;
  } catch (error) {
    console.error('Failed to create Base64 query string:', error);
    return ''; // Return an empty string or handle the error as needed.
  }
};

type TSrcValues =
  | 'CommentAttachmentSearchResult'
  | 'CommentSearchResult'
  | 'TicketAttachmentSearchResult'
  | 'TicketSearchResult';
type TSrcParam = TSrcValues | TSrcValues[];

export const getHighlightFromSearch = (
  search: URLSearchParams,
  source: TSrcParam,
): { type: string | null; match: string } => {
  try {
    const rawHighlight = search.get('highlight') || '';
    if (!rawHighlight) return { type: null, match: '' };

    const highlight: { type: string; matches: string[] } = JSON.parse(atob(rawHighlight));

    if (Array.isArray(source))
      return source.includes(highlight.type as TSrcValues)
        ? { type: highlight.type, match: highlight.matches[0] }
        : { type: null, match: '' };
    return highlight.type === source
      ? { type: highlight.type, match: highlight.matches[0] }
      : { type: null, match: '' };
  } catch {
    console.warn('Error parsing highlight');
  }
  return { type: null, match: '' };
};

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export type DeepMergeOverride<T, U> = {
  [K in keyof T | keyof U]: K extends keyof U
    ? K extends keyof T
      ? T[K] extends object
        ? U[K] extends object
          ? DeepMergeOverride<T[K], U[K]> // Recursive merge
          : U[K] // U overrides T
        : U[K]
      : U[K]
    : K extends keyof T
    ? T[K]
    : never;
};

// Reusable debounce function
//  Delays running `fn` until `delay` ms have passed since the last call.
export const debounce = (fn: any, delay = 300) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<typeof fn>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export const toggleStringItemInArray = (arr: string[], item: string) => {
  if (arr.includes(item)) return arr.filter((el) => el !== item);
  return [...arr, item];
};

export const formatEndDate = (endDate: string | null) => {
  let rawEndDate;
  if (endDate) {
    rawEndDate = dayjs(endDate).toDate().toLocaleDateString('en-US').toString();
  }
  const getDateVal = rawEndDate ? new Date(rawEndDate) : null;
  return getDateVal?.getTime() ?? null;
};

/**
 * Returns an array of keys from the given object whose values are strictly `true`.
 *
 * Useful for filtering enabled or active elements from a configuration or flag object.
 *
 * @typeParam T - The shape of the input object, where values may be boolean or other types.
 * @param obj - An object whose keys represent elements and values are boolean flags.
 * @returns An array of keys from `obj` whose values are `true`.
 */
export const getEnabledElements = <T extends Record<string, boolean>>(obj: T): Array<keyof T> => {
  return Object.keys(obj).filter((key) => obj[key] === true) as Array<keyof T>;
};

export const getTotalDefinedProps = <T extends object>(obj: T): number => {
  if (!obj) return 0;

  return Object.values(obj).filter((value) => value !== undefined).length;
};

/**
 * Determines whether the content of an inner HTML element overflows the available width of an outer HTML element.
 *
 * Calculates the available width of the outer element by subtracting its left and right paddings from its client width,
 * then compares it to the scroll width of the inner element to check for overflow.
 *
 * @param outerEl - The outer HTML element that defines the visible area.
 * @param innerEl - The inner HTML element containing the content to check for overflow.
 * @returns `true` if the inner element's content overflows the available width of the outer element, otherwise `false`.
 */
export const contentHasOverflow = (outerEl: HTMLElement, innerEl: HTMLElement): boolean => {
  const outerWidth = outerEl.clientWidth;
  const innerWidth = innerEl.scrollWidth;
  const style = window.getComputedStyle(outerEl);
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingRight = parseFloat(style.paddingRight) || 0;
  const availableWidth = outerWidth - paddingLeft - paddingRight;
  const hasOverflow = innerWidth > availableWidth;
  return hasOverflow;
};

/**
 * Creates an object composed of the picked object properties.
 *
 * @template T The type of the source object.
 * @template K The keys to pick from the source object.
 * @param obj The source object.
 * @param keys The property keys to pick.
 * @returns A new object with only the specified properties.
 *
 * @example
 * const object = { a: 1, b: '2', c: 3 };
 * pick(object, ['a', 'c']);
 * // => { a: 1, c: 3 }
 */
export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result = {} as Pick<T, K>;

  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });

  return result;
};

/**
 * Find if one of the errors match the code parameter
 * @param errorList List of errors
 * @param code Code to compare
 * @returns
 */
export const hasGraphQLError = (errorList: ApolloError['graphQLErrors'] = [], code: string): boolean => {
  for (const error of errorList) {
    if ('code' in error) {
      if (error.code === code) return true;
    }
  }

  return false;
};

export const generateRedirect = (pathname: string, search = '', hash = '') => {
  if (pathname.startsWith('/app') || pathname.startsWith('/docs') || pathname.startsWith('/support'))
    return `?redirect=${encodeURIComponent(`${pathname}${search}${hash}`)}`;
  return '';
};

export const formatWorkstream = (w: string) => {
  if (w === 'Android_TV') return 'Android';
  if (w === 'Billing_API') return 'Partner API';
  return w.replaceAll('_', ' ');
};

/**
 * Builds the switchUserType endpoint URL.
 *
 * Locally (NODE_ENV === 'development') the dev server proxies `/api`, so we use a
 * relative path; in deployed environments we hit the API base URI directly.
 *
 * @param userType - The user type to switch to (e.g. 'Disney' or 'Partner').
 * @returns The fully-qualified switchUserType URL for the current environment.
 */
export const getSwitchUserTypeUrl = (userType: string): string =>
  process.env.NODE_ENV === 'development'
    ? `/api/jwt/switchUserType?userType=${userType}`
    : `${process.env.REACT_APP_API_BASE_URI}/jwt/switchUserType?userType=${userType}`;
